import type { Language } from '@eformulieren/engine'
import type { BelangPartij, BelangPartijen } from '@eformulieren/model'
import { type FormEvent, type ReactNode, useId, useRef, useState } from 'react'
import { BusyButton } from './BusyButton.js'
import type { Bilingual } from '../language/text.js'

/**
 * De belangenverklaring als eigen control (besluit van de gebruiker van 30 oktober 2026, R-184d,
 * R-184g, R-184h, R-139, R-140).
 *
 * Wie om advies wordt gevraagd, verklaart eerst of hij een belang heeft, en ziet daarvoor alleen bij
 * wie dat belang zou liggen: de titel van de aanvraag, de aanvrager en de mede-aanvragers, met hun
 * organisatie als die bekend is. Dat komt als `partijen` van de pod (`GET /advies`, de vorm
 * `BelangPartijen` in het model); de control toont wat er is en verzint niets. Ontbreekt alles, dan zegt
 * hij dat, zodat de adviseur weet dat hij het in de uitnodiging moet nagaan.
 *
 * **Een control en geen stap van één pagina.** De adviseurpagina gebruikt hem, en een
 * beoordelingsformulier of het bouwblok adviesrapport kan hem later net zo gebruiken: hij kent geen
 * route en geen verzoek, alleen de partijen en een handeling die een belofte teruggeeft. De vraag en
 * het anker toont de pagina zelf, boven de control.
 *
 * **Toegankelijk** (R-139, R-140): de keuze is een `fieldset` met `legend`; zonder keuze versturen geeft
 * een melding aan de groep (`aria-describedby`, `role="alert"`) en zet de focus op de eerste keuze. De
 * toelichting verschijnt alleen bij een belang, en is optioneel. Versturen gaat langs `BusyButton`:
 * direct bezig, geen tweede verzending, en bij een fout weer zoals het was met de melding aan de knop.
 */

/** Wat de adviseur verklaart; de vorm die `POST /advies/:id/belangenverklaring` neemt. */
export interface InterestDeclarationOutcome {
  readonly uitkomst: 'geen-belang' | 'belang'
  /** Alleen bij een belang, en null als er niets is ingevuld. */
  readonly toelichting: string | null
}

export interface InterestDeclarationProps {
  readonly taal: Language
  /** Wie de verklaring noemt, zoals de pod ze levert; null als het formulier geen koppeling heeft. */
  readonly partijen: BelangPartijen | null
  /** Verstuurt de verklaring. Zolang de belofte loopt staat de knop bezig; een afwijzing toont de foutmelding. */
  readonly onDeclare: (verklaring: InterestDeclarationOutcome) => Promise<unknown>
  /** Het niveau van de kop, zodat de control past onder de kop van de pagina die hem gebruikt. */
  readonly kopNiveau?: 2 | 3
  /** De melding als versturen mislukt; zonder deze een algemene. */
  readonly foutmelding?: string
}

/** De lengte van de toelichting, gelijk aan de grens in het model (`Belangenverklaring`). */
export const TOELICHTING_MAX = 2000

const T = {
  kop: { nl: 'Belangenverklaring', en: 'Declaration of interest' },
  inleiding: {
    nl: 'Om te beoordelen of u een belang heeft:',
    en: 'To judge whether you have an interest:',
  },
  titel: { nl: 'Titel van de aanvraag', en: 'Title of the application' },
  aanvrager: { nl: 'Aanvrager', en: 'Applicant' },
  medeAanvragers: { nl: 'Mede-aanvragers', en: 'Co-applicants' },
  onbekend: {
    nl: 'Bij dit verzoek zijn de titel en de aanvragers niet bekend. Kijk in de uitnodiging over welke aanvraag het gaat voordat u verklaart.',
    en: 'The title and the applicants are not known for this request. Check the invitation to see which application it concerns before you declare.',
  },
  uitlegKop: { nl: 'Wat is een belang?', en: 'What is an interest?' },
  uitleg: {
    nl: 'U hebt een belang als u een band hebt met de aanvraag of met een van de aanvragers die uw oordeel kan kleuren, of die anderen zo kunnen zien. Bijvoorbeeld: u werkt bij dezelfde organisatie, u werkt met een aanvrager samen, u bent familie of bevriend, of u hebt zelf voordeel bij de uitkomst.',
    en: 'You have an interest if you have a tie with the application or with one of the applicants that could colour your judgement, or that others could see that way. For example: you work at the same organisation, you work together with an applicant, you are related or friends, or you would gain from the outcome yourself.',
  },
  keuze: { nl: 'Heeft u een belang?', en: 'Do you have an interest?' },
  geenBelang: { nl: 'Ik heb geen belang', en: 'I have no interest' },
  geenBelangGevolg: {
    nl: 'U ziet daarna wat met u is gedeeld en kunt advies geven.',
    en: 'You then see what has been shared with you and can give advice.',
  },
  belang: { nl: 'Ik heb een belang', en: 'I have an interest' },
  belangGevolg: {
    nl: 'Het verzoek eindigt dan. U krijgt geen toegang, en wie u om advies vroeg hoort dat van NWO.',
    en: 'The request then ends. You get no access, and whoever asked you for advice hears this from NWO.',
  },
  toelichting: { nl: 'Toelichting (niet verplicht)', en: 'Explanation (optional)' },
  toelichtingUitleg: {
    nl: 'Wat is uw belang? Dit gaat naar wie u om advies vroeg.',
    en: 'What is your interest? This goes to whoever asked you for advice.',
  },
  kiesEerst: { nl: 'Kies of u een belang heeft.', en: 'Choose whether you have an interest.' },
  versturen: { nl: 'Verklaring versturen', en: 'Send declaration' },
  bezig: { nl: 'Bezig met versturen…', en: 'Sending…' },
  fout: {
    nl: 'Uw verklaring is niet verstuurd. Probeer het opnieuw.',
    en: 'Your declaration has not been sent. Please try again.',
  },
} as const satisfies Readonly<Record<string, Bilingual>>

/** Een partij als één regel: naam, en de organisatie erachter als die bekend is. */
function line(party: BelangPartij): string {
  return party.organisatie === null ? party.naam : `${party.naam}, ${party.organisatie}`
}

export function InterestDeclaration({ taal: language, partijen: parties, onDeclare, kopNiveau = 2, foutmelding }: InterestDeclarationProps): ReactNode {
  const [choice, setChoice] = useState<InterestDeclarationOutcome['uitkomst'] | null>(null)
  const [explanation, setExplanation] = useState('')
  const [missing, setMissing] = useState(false)
  const firstChoice = useRef<HTMLInputElement>(null)
  const id = useId()
  const Heading = kopNiveau === 2 ? 'h2' : 'h3'
  const Subheading = kopNiveau === 2 ? 'h3' : 'h4'

  const known = parties !== null && (parties.titel !== null || parties.aanvrager !== null || parties.medeAanvragers.length > 0)

  function send(): Promise<unknown> | undefined {
    if (choice === null) {
      setMissing(true)
      firstChoice.current?.focus()
      return undefined
    }
    const trimmed = explanation.trim()
    return onDeclare({ uitkomst: choice, toelichting: choice === 'belang' && trimmed !== '' ? trimmed : null })
  }

  function choose(next: InterestDeclarationOutcome['uitkomst']): void {
    setChoice(next)
    setMissing(false)
  }

  return (
    <section className="ef-belang" lang={language} aria-labelledby={`${id}-kop`}>
      <Heading id={`${id}-kop`} className="ef-belang__kop">
        {T.kop[language]}
      </Heading>

      {known && parties !== null ? (
        <div className="ef-belang__partijen">
          <p className="ef-belang__tekst">{T.inleiding[language]}</p>
          <dl className="ef-belang__lijst">
            {parties.titel === null ? null : (
              <div className="ef-belang__regel">
                <dt>{T.titel[language]}</dt>
                <dd>{parties.titel}</dd>
              </div>
            )}
            {parties.aanvrager === null ? null : (
              <div className="ef-belang__regel">
                <dt>{T.aanvrager[language]}</dt>
                <dd>{line(parties.aanvrager)}</dd>
              </div>
            )}
            {parties.medeAanvragers.length === 0 ? null : (
              <div className="ef-belang__regel">
                <dt>{T.medeAanvragers[language]}</dt>
                <dd>
                  <ul className="ef-belang__namen">
                    {parties.medeAanvragers.map((party, index) => (
                      <li key={index}>{line(party)}</li>
                    ))}
                  </ul>
                </dd>
              </div>
            )}
          </dl>
        </div>
      ) : (
        <p className="ef-belang__tekst">{T.onbekend[language]}</p>
      )}

      <div className="ef-belang__uitleg">
        <Subheading className="ef-belang__subkop">{T.uitlegKop[language]}</Subheading>
        <p className="ef-belang__tekst">{T.uitleg[language]}</p>
      </div>

      <form
        className="ef-belang__formulier"
        noValidate
        onSubmit={(event: FormEvent) => {
          // De knop verstuurt zelf (BusyButton); Enter in het formulier doet niets dubbel.
          event.preventDefault()
        }}
      >
        <fieldset className="ef-belang__keuzes" aria-describedby={missing ? `${id}-mist` : undefined}>
          <legend className="ef-belang__legenda">{T.keuze[language]}</legend>
          <div className="ef-belang__keuze">
            <input
              ref={firstChoice}
              id={`${id}-geen-keuze`}
              type="radio"
              name={`${id}-uitkomst`}
              value="geen-belang"
              checked={choice === 'geen-belang'}
              aria-describedby={`${id}-geen`}
              onChange={() => choose('geen-belang')}
            />
            <span className="ef-belang__keuzetekst">
              <label htmlFor={`${id}-geen-keuze`} className="ef-belang__keuzelabel">
                {T.geenBelang[language]}
              </label>
              <span id={`${id}-geen`} className="ef-belang__gevolg">
                {T.geenBelangGevolg[language]}
              </span>
            </span>
          </div>
          <div className="ef-belang__keuze">
            <input
              id={`${id}-wel-keuze`}
              type="radio"
              name={`${id}-uitkomst`}
              value="belang"
              checked={choice === 'belang'}
              aria-describedby={`${id}-wel`}
              onChange={() => choose('belang')}
            />
            <span className="ef-belang__keuzetekst">
              <label htmlFor={`${id}-wel-keuze`} className="ef-belang__keuzelabel">
                {T.belang[language]}
              </label>
              <span id={`${id}-wel`} className="ef-belang__gevolg">
                {T.belangGevolg[language]}
              </span>
            </span>
          </div>
          {missing ? (
            <p id={`${id}-mist`} role="alert" className="ef-belang__melding">
              {T.kiesEerst[language]}
            </p>
          ) : null}
        </fieldset>

        {choice === 'belang' ? (
          <div className="ef-belang__toelichting">
            <label htmlFor={`${id}-toelichting`} className="ef-belang__label">
              {T.toelichting[language]}
            </label>
            <p id={`${id}-toelichting-uitleg`} className="ef-belang__gevolg">
              {T.toelichtingUitleg[language]}
            </p>
            <textarea
              id={`${id}-toelichting`}
              className="ef-belang__invoer"
              rows={4}
              maxLength={TOELICHTING_MAX}
              aria-describedby={`${id}-toelichting-uitleg`}
              value={explanation}
              onChange={(event) => setExplanation(event.target.value)}
            />
          </div>
        ) : null}

        <div className="ef-belang__acties">
          <BusyButton className="ef-actieknop" data-soort="primair" busyText={T.bezig[language]} errorText={foutmelding ?? T.fout[language]} onAction={send}>
            {T.versturen[language]}
          </BusyButton>
        </div>
      </form>
    </section>
  )
}
